import { RateLimiter } from '@shared/utils/rate-limiter';
import { Auth } from '@shared/Auth';
import { IModelInfo } from './types';
import { Ajax2 } from '@shared/Ajax';
import { z } from 'zod';

export interface TileInfo {
    tileCount: number;
    maxZoom: number;
    timestamp: number;
    rasterUri: string;
    vectorUri: string;
}

export interface DrawingInfo {
    projectUuid: string;
    fileUuid: string;
    tileInfo?: TileInfo;
    geojsonObjectKeys: string[];
}

const ModelStatusSchema = z.object({
    project_uuid: z.string(),
    uuid: z.string(),
    available: z.boolean().default(false),
    simplified_available: z.boolean().default(false),
    pointcloud_available: z.boolean().default(false),
    mesh_image_available: z.boolean().default(false),
    tiled_image_available: z.boolean().default(false),
    outlines_available: z.boolean().default(false),
    processing_requested: z.number().nullable(),
    processing_started: z.number().nullable(),
    processing_completed: z.number().nullable(),
    is_in_processing: z.boolean().default(false),
    has_failed: z.boolean().default(false),
});

export type IModelStatus = z.infer<typeof ModelStatusSchema>;

const RATE_LIMITER = new RateLimiter(5, 7);

const getVizApiBaseUrl = (): string => {
    const isLocal: boolean = kuura.getServerDomain().includes('localhost');

    if (isLocal) {
        return 'http://localhost:8001/viz/v1';
    }

    const isBeta: boolean = kuura.getServerDomain().includes('beta');
    return isBeta ? kuura.AwsApiGatewayDomain() + '/viz/beta/v1' : kuura.AwsApiGatewayDomain() + '/viz/v1';
};

const ModelStructureResponseSchema = z.object({
    status: z.boolean(),
    structure: z.boolean(),
});

type ModelStructureResponse = z.infer<typeof ModelStructureResponseSchema>;

const isModelStructure = async (modelUuid: string): Promise<boolean> => {
    const response: ModelStructureResponse = await Ajax2({
        url: `${kuura.getContextPath()}/api/1/model/${modelUuid}/is-structure`,
    });

    if (!response.status) {
        console.warn('Failed to determine if model is structure', modelUuid);
        return false;
    }

    return response.structure;
};

const getModelLayerCounts = async (modelUuid: string): Promise<ILayerCounts | null> => {
    const response = await Ajax2({
        url: `${kuura.getContextPath()}/api/1/model/${modelUuid}`,
    });

    if (!response.status) {
        return null;
    }

    const modelData = response['model'];

    if (!modelData) {
        return null;
    }

    const layerCounts = modelData['layerCounts'];

    if (!layerCounts) {
        return null;
    }

    return layerCounts;
};

const getModelInfoByHeadUuid = async (headUuid: string): Promise<IModel | null> => {
    const response = await Ajax2({
        url: `${kuura.getContextPath()}/api/1/model/head/${headUuid}`,
    });

    if (!response.status) {
        return null;
    }

    const modelData = response['model'];

    if (!modelData) {
        return null;
    }

    return modelData;
};

const modelHasBeenProcessedInvalidly = (status: IModelStatus): boolean => {
    return status.available && (!status.simplified_available || !status.pointcloud_available || !status.mesh_image_available);
};

const meshImageHasFailed = (status: IModelStatus, processingStartTime?: Date): boolean => {
    if (processingStartTime) {
        const processingDuration = new Date().getTime() - processingStartTime.getTime();
        if (processingDuration > 15 * 60 * 1000) {
            return status.available && !status.mesh_image_available; // Processing took too long
        } else {
            return false; // Processing is still ongoing
        }
    }

    return status.available && !status.mesh_image_available;
};

const getModelStatus = async (uuid: string): Promise<IModelStatus> => {
    const tokenReturn = await RATE_LIMITER.requestToken();
    const baseUrl = getVizApiBaseUrl();
    const url = `${baseUrl}/${kuura.getProjectUuid()}/${uuid}/status`;
    const token = await Auth.getToken();

    try {
        const resp = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token.accessToken}`,
            },
        });

        const json: unknown = await resp.json();
        const result = ModelStatusSchema.safeParse(json);

        if (!result.success) {
            console.warn('Invalid model status response for', uuid, result.error);
            return null;
        }

        return result.data;
    } catch (e) {
        console.warn('Could not find model status for', uuid);
        return null;
    } finally {
        tokenReturn();
    }
};

const getDrawingStatus = async (uuid: string): Promise<any> => {
    const tokenReturn = await RATE_LIMITER.requestToken();
    const baseUrl = getVizApiBaseUrl();
    const url = `${baseUrl}/drawings/${kuura.getProjectUuid()}/${uuid}/status`;
    const token = await Auth.getToken();

    try {
        const resp = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token.accessToken}`,
            },
        });

        const json = await resp.json();
        return json;
    } catch (e) {
        console.warn('Could not find model status for', uuid);
        return null;
    } finally {
        tokenReturn();
    }
};

const getPMTilesUrl = async (uuid: string): Promise<string | null> => {
    const tokenReturn = await RATE_LIMITER.requestToken();
    const baseUrl = getVizApiBaseUrl();
    const url = `${baseUrl}/drawings/${kuura.getProjectUuid()}/${uuid}/pmtiles-url`;
    const token = await Auth.getToken();

    try {
        const resp = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token.accessToken}`,
            },
        });

        if (!resp.ok) {
            return null;
        }

        const json = await resp.json();
        return json['url'];
    } catch (e) {
        console.warn('Could not find model status for', uuid);
        return null;
    } finally {
        tokenReturn();
    }
};

const getModelInfo = async (uuid: string): Promise<IModelInfo> => {
    const tokenReturn = await RATE_LIMITER.requestToken();
    const baseUrl = getVizApiBaseUrl();
    const url = `${baseUrl}/${kuura.getProjectUuid()}/${uuid}/info`;
    const token = await Auth.getToken();

    try {
        const resp = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token.accessToken}`,
            },
        });

        const json = await resp.json();
        return json;
    } catch (e) {
        console.warn('Could not find model info for', uuid);
        return null;
    } finally {
        tokenReturn();
    }
};

const getDrawingInfo = async (uuid: string): Promise<DrawingInfo> => {
    const tokenReturn = await RATE_LIMITER.requestToken();
    const baseUrl = getVizApiBaseUrl();
    const url = `${baseUrl}/drawings/${kuura.getProjectUuid()}/${uuid}/info`;
    const token = await Auth.getToken();

    try {
        const resp = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token.accessToken}`,
            },
        });

        const json = await resp.json();
        return json;
    } catch (e) {
        console.warn('Could not find drawing info for', uuid);
        return null;
    } finally {
        tokenReturn();
    }
};

const pollModelUntilReady = (uuid: string): Promise<IModelStatus> => {
    return new Promise((resolve, reject) => {
        const pollInterval = setInterval(async () => {
            const status: IModelStatus = await getModelStatus(uuid);
            if (status.processing_completed || status.has_failed) {
                clearInterval(pollInterval);
                resolve(status);
            }
        }, 20 * 1000);
    });
};

export const ModelService = {
    getModelInfo,
    getModelStatus,
    getVizApiBaseUrl,
    getDrawingInfo,
    modelHasBeenProcessedInvalidly,
    meshImageHasFailed,
    pollModelUntilReady,
    getModelLayerCounts,
    getModelInfoByHeadUuid,
    getDrawingStatus,
    getPMTilesUrl,
    isModelStructure,
};
